/**
 * API Rate Limiter
 * Tracks and limits API usage to prevent exceeding free tier limits
 */

interface UsageData {
  usage: number
  limit: number
  lastReset: string
}

export class ApiRateLimiter {
  private storageKey: string
  private monthlyLimit: number

  constructor(providerName: string, monthlyLimit: number) {
    this.storageKey = `mt_usage_${providerName}`
    this.monthlyLimit = monthlyLimit
    this.initializeStorage()
  }

  /**
   * Initialize storage with default values if not exists
   */
  private initializeStorage(): void {
    const data = this.getStorageData()
    if (!data) {
      this.resetUsage()
    } else {
      // Check if we need to reset (new month)
      this.checkAndResetIfNeeded(data)
    }
  }

  /**
   * Get usage data from localStorage
   */
  private getStorageData(): UsageData | null {
    try {
      const data = localStorage.getItem(this.storageKey)
      return data ? JSON.parse(data) : null
    } catch (error) {
      console.error('Error reading rate limit data:', error)
      return null
    }
  }

  /**
   * Save usage data to localStorage
   */
  private setStorageData(data: UsageData): void {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(data))
    } catch (error) {
      console.error('Error saving rate limit data:', error)
    }
  }

  /**
   * Check if we need to reset usage (new month)
   */
  private checkAndResetIfNeeded(data: UsageData): void {
    const lastReset = new Date(data.lastReset)
    const now = new Date()
    
    // Reset if different month or year
    if (
      lastReset.getMonth() !== now.getMonth() ||
      lastReset.getFullYear() !== now.getFullYear()
    ) {
      console.log(`📅 New month detected, resetting ${this.storageKey} usage`)
      this.resetUsage()
    }
  }

  /**
   * Check if a request can be made
   */
  canMakeRequest(characterCount: number): boolean {
    const data = this.getStorageData()
    if (!data) {
      return true
    }

    this.checkAndResetIfNeeded(data)
    
    return (data.usage + characterCount) <= data.limit
  }

  /**
   * Track API usage (add characters used)
   */
  trackUsage(characterCount: number): void {
    const data = this.getStorageData()
    if (!data) {
      this.resetUsage()
      return
    }

    this.checkAndResetIfNeeded(data)

    const updatedData: UsageData = {
      ...data,
      usage: data.usage + characterCount
    }

    this.setStorageData(updatedData)

    // Log warning if approaching limit
    const percentage = (updatedData.usage / updatedData.limit) * 100
    if (percentage >= 80 && percentage < 90) {
      console.warn(`⚠️ ${this.storageKey}: ${percentage.toFixed(0)}% of monthly limit used`)
    } else if (percentage >= 90) {
      console.error(`🚨 ${this.storageKey}: ${percentage.toFixed(0)}% of monthly limit used!`)
    }
  }

  /**
   * Get current usage
   */
  getUsage(): number {
    const data = this.getStorageData()
    if (!data) {
      return 0
    }
    this.checkAndResetIfNeeded(data)
    return data.usage
  }

  /**
   * Get monthly limit
   */
  getLimit(): number {
    return this.monthlyLimit
  }

  /**
   * Get usage percentage
   */
  getUsagePercentage(): number {
    const usage = this.getUsage()
    return Math.round((usage / this.monthlyLimit) * 100)
  }

  /**
   * Reset usage to zero (new month or manual reset)
   */
  resetUsage(): void {
    const data: UsageData = {
      usage: 0,
      limit: this.monthlyLimit,
      lastReset: new Date().toISOString()
    }
    this.setStorageData(data)
    console.log(`✅ ${this.storageKey} usage reset`)
  }

  /**
   * Get remaining quota
   */
  getRemainingQuota(): number {
    const usage = this.getUsage()
    return Math.max(0, this.monthlyLimit - usage)
  }

  /**
   * Check if limit is exceeded
   */
  isLimitExceeded(): boolean {
    const usage = this.getUsage()
    return usage >= this.monthlyLimit
  }

  /**
   * Get days until reset
   */
  getDaysUntilReset(): number {
    const now = new Date()
    const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1)
    const diffTime = nextMonth.getTime() - now.getTime()
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24))
  }

  /**
   * Get detailed status
   */
  getStatus() {
    const data = this.getStorageData()
    if (!data) {
      return {
        usage: 0,
        limit: this.monthlyLimit,
        percentage: 0,
        remaining: this.monthlyLimit,
        daysUntilReset: this.getDaysUntilReset(),
        lastReset: new Date().toISOString()
      }
    }

    this.checkAndResetIfNeeded(data)

    return {
      usage: data.usage,
      limit: data.limit,
      percentage: this.getUsagePercentage(),
      remaining: this.getRemainingQuota(),
      daysUntilReset: this.getDaysUntilReset(),
      lastReset: data.lastReset
    }
  }
}

export default ApiRateLimiter